import { ChangeDetectionStrategy, Component, HostListener, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalStorageService } from '@app/core/services/local-storage.service';
import { RawFileStoreService } from '@app/core/services/raw-file-store.service';
import { SEOService } from '@app/core/services/seo.service';
import * as fromRoot from '@app/reducers';
import { FileDropEntry } from '@app/shared/components/smash-file-drop/file-drop-entry';
import { FileSystemMetadata } from '@app/shared/components/smash-file-drop/interfaces';
import { getPathWithoutLastFragment } from '@app/shared/helpers/location';
import { Identity } from '@app/shared/models/identity';
import { DeliveryType, EmailDelivery, LinkDelivery } from '@app/shared/models/transferConfiguration';
import { DialogService } from '@ngneat/dialog';
import { Store } from '@ngrx/store';
import { GetRecordOutput } from '@smash-sdk/domain/01-2024';
import { Observable, Subscription } from 'rxjs';
import { filter, first, withLatestFrom } from 'rxjs/operators';
import { RawFileError } from '../../interfaces/file';
import { LayoutActions, PrepareActions } from '../../store/actions';
import * as fromUploader from '../../store/reducers';
import { ModalForbiddenEmailComponent } from './modals/modal-forbidden-email/modal-forbidden-email.component';
import { ModalForbiddenSubscriptionComponent } from './modals/modal-forbidden-subscription/modal-forbidden-subscription.component';
import { ModalPasswordRequiredComponent } from './modals/modal-password-required/modal-password-required.component';
import { ModalUnknownUploadErrorComponent } from './modals/modal-unknown-upload-error/modal-unknown-upload-error.component';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-prepare',
  templateUrl: './prepare.component.html',
  styleUrls: ['./prepare.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrepareComponent implements OnInit, OnDestroy {
  identity$: Observable<Identity>;
  rawFiles$: Observable<FileSystemMetadata[]>;
  totalSizeToUpload$: Observable<number>;
  uploadFormMode$: Observable<string>;
  domain$: Observable<GetRecordOutput['record']>;
  canConfigSmash$: Observable<boolean>;
  domainTransferSettings$: Observable<any>;
  rawFilesError$: Observable<RawFileError>;
  uploadError$: Observable<any>;

  invalidAvailabilityDurationError = 'InvalidAvailabilityDurationError'; // temporary fix
  subscriptions: Subscription[] = [];

  constructor(
    private store$: Store<fromRoot.State & fromUploader.State>,
    private router: Router,
    private rawFileStoreService: RawFileStoreService,
    private dialog: DialogService,
    private route: ActivatedRoute,
    private seoService: SEOService,
    private readonly localStorageService: LocalStorageService,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {

    if (isPlatformBrowser(this.platformId)) {
      this.store$.dispatch(PrepareActions.resetDrawer());

      this.rawFiles$ = this.store$.select(fromUploader.getAllRawFiles);
      this.identity$ = this.store$.select(fromRoot.getActiveIdentity);
      this.domain$ = this.store$.select(fromRoot.getCurrentDomain);
      this.uploadFormMode$ = this.store$.select(fromUploader.getUploadFormMode);
      this.totalSizeToUpload$ = this.store$.select(fromUploader.getTransferSize);
      this.uploadError$ = this.store$.select(fromUploader.getUploadError);
      this.rawFilesError$ = this.store$.select(fromUploader.getRawFilesError);

      this.canConfigSmash$ = this.store$.select(fromRoot.canConfigSmash);
      this.domainTransferSettings$ = this.store$.select(fromRoot.getDomainTransferSettings);

      this.subscriptions.push(
        this.rawFiles$.subscribe(files => {
          if (!files.length) {
            this.router.navigate([getPathWithoutLastFragment()], { relativeTo: this.route });
          }
        }),
        this.uploadError$.pipe(filter(error => error !== null)).subscribe((error: any) => {
          if (error?.name === 'EmailNotAllowedError') {
            this.openModalForbiddenEmail({ requestId: error?.requestId });
          } else if (error?.name === 'InvalidSubscriptionError') {
            this.openModalForbiddenSubscription({ requestId: error?.requestId });
          } else if (error?.name === 'PasswordRequiredError') {
            this.openModalPasswordRequired({ requestId: error?.requestId });
          } else if (error?.name === 'UnknownError') {
            this.openUnknownErrorModal({
              errorName: error?.name,
              errorMessage: error?.message,
              requestId: error?.requestId
            });
          }
        }),
      );
    }
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.loadDeliveryParameters();
      this.loadDeliveryParametersFromQueryString();
      this.seoService.setLandingTitle();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.map(subscription => subscription.unsubscribe());
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any) {
    $event.returnValue = true;
  }

  fileOver() {
    this.store$.dispatch(LayoutActions.UpdateFileOver({ fileOver: true }));
  }

  fileLeave() {
    this.store$.dispatch(LayoutActions.UpdateFileOver({ fileOver: false }));
  }

  async dropped(files: FileDropEntry[]) {
    // tslint:disable-next-line:no-shadowed-variable
    const identity = await this.identity$.pipe(first(identity => !!identity)).toPromise();
    await this.rawFileStoreService.setFiles(files, identity);
  }

  async openModalForbiddenEmail({ requestId }: { requestId: string }): Promise<void> {
    // tslint:disable-next-line:no-shadowed-variable
    const domain = await this.domain$.pipe(first(domain => !!domain)).toPromise();
    this.dialog.open(ModalForbiddenEmailComponent,
      {
        closeButton: false,
        enableClose: true,
        size: 'xs',
        data: {
          domain,
          requestId,
        },
      }
    );
  }

  openModalForbiddenSubscription({ requestId }: { requestId: string }): void {
    this.dialog.open(ModalForbiddenSubscriptionComponent,
      {
        closeButton: false,
        enableClose: true,
        size: 'sm',
        data: {
          requestId,
        },
      });
  }

  openModalPasswordRequired({ requestId }: { requestId: string }): void {
    this.dialog.open(ModalPasswordRequiredComponent,
      {
        closeButton: false,
        enableClose: true,
        size: 'xs',
        data: {
          requestId,
        },
      });
  }

  openUnknownErrorModal({ errorName, errorMessage, requestId }: { errorName: string, errorMessage: string, requestId: string }): void {
    this.dialog.open(ModalUnknownUploadErrorComponent,
      {
        closeButton: false,
        enableClose: true,
        size: 'xs',
        data: {
          errorName,
          errorMessage,
          requestId,
        },
      });
  }

  onFileOver(over) {
    if (over) {
      this.store$.dispatch(LayoutActions.UpdateFileOver({ fileOver: true }));
    } else {
      this.store$.dispatch(LayoutActions.UpdateFileOver({ fileOver: false }));
    }
  }

  loadDeliveryParameters() {
    this.identity$.pipe(
      first(),
      withLatestFrom(this.domain$),
    ).subscribe(([identity, domain]) => {
      const lastDeliveryParameters = this.getLastDeliveryParameters();

      // Set the upload form mode based on the last delivery parameters or the default value if the last delivery parameters are not valid anymore.
      // Example : if the user has previously selected a delivery type that is not available anymore.
      if (lastDeliveryParameters?.type && typeof lastDeliveryParameters?.type === 'string') {
        const options = domain?.options?.transfer?.delivery?.options as DeliveryType[];
        // FIX ME It should be the default value from the domain but it is not the correct one for now.
        // Example : admin remove the email delivery option from the domain. The default value should be the link delivery. But it is not the case.
        const defaultUploadFormMode = domain?.options?.transfer?.delivery?.options[0] as DeliveryType;
        const uploadFormMode = options?.includes(lastDeliveryParameters?.type) ? lastDeliveryParameters?.type : defaultUploadFormMode;
        this.store$.dispatch(PrepareActions.setUploadFormMode({ uploadFormMode }));
      }

      if (identity?.username) {
        this.store$.dispatch(PrepareActions.SetTransferSender({ sender: { email: identity?.username } }));
      } else if (lastDeliveryParameters?.sender) {
        this.store$.dispatch(PrepareActions.SetTransferSender({ sender: lastDeliveryParameters?.sender }));
      }
    });
  }

  getLastDeliveryParameters(): EmailDelivery | LinkDelivery | undefined {
    const lastDeliveryParametersString: string = this.localStorageService.getItem('lastTransferParameters');
    if (lastDeliveryParametersString) {
      return JSON.parse(decodeURIComponent(escape(atob(lastDeliveryParametersString)))) as EmailDelivery | LinkDelivery;
    } else {
      return undefined;
    }
  }

  loadDeliveryParametersFromQueryString() {
    this.identity$.pipe(
      first(),
      withLatestFrom(this.domain$),
    ).subscribe(([identity, domain]) => {
      const prefilledTransferParametersString: string = this.localStorageService.getItem('prefilledTransferParameters');
      if (prefilledTransferParametersString) {
        const params = JSON.parse(prefilledTransferParametersString);
        if (params) {
          const { sender, name, receivers, uploadFormMode, description, title } = params;
          const options = domain?.options?.transfer?.delivery?.options as DeliveryType[] || [];
          if (uploadFormMode && options.includes(uploadFormMode as DeliveryType)) {

            this.store$.dispatch(PrepareActions.setUploadFormMode({ uploadFormMode }));
          }
          if (sender && !identity?.username) {
            this.store$.dispatch(PrepareActions.SetTransferSender({ sender: { email: sender, name } }));
          }
          if (description) {
            this.store$.dispatch(PrepareActions.SetEmailDescription({ description }));
          }
          if (title) {
            this.store$.dispatch(PrepareActions.SetEmailTitle({ title }));
            this.store$.dispatch(PrepareActions.SetLinkTitle({ title }));
          }
          if (receivers) {
            this.store$.dispatch(PrepareActions.SetEmailReceivers({ receivers }));
          }
        }
      }
    });
  }
}
